type CommitDate = {
  date: string;
};

export type ChangedMonthDirection = 'newer' | 'older';

const MONTH_KEY_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

const padTwoDigits = (value: number): string => String(value).padStart(2, '0');

const parseCommitDate = (dateString: string): Date | null => {
  const date = new Date(dateString);
  return Number.isFinite(date.getTime()) ? date : null;
};

const toLocalMonthKey = (date: Date): string =>
  `${date.getFullYear()}-${padTwoDigits(date.getMonth() + 1)}`;

/**
 * 新しい順のコミット履歴から、変更があるローカル年月を新しい順で返す。
 */
export const getChangedMonths = (commits: readonly CommitDate[]): string[] => {
  const months = new Set<string>();

  commits.forEach(commit => {
    const date = parseCommitDate(commit.date);
    if (date) {
      months.add(toLocalMonthKey(date));
    }
  });

  return [...months].sort((left, right) => right.localeCompare(left));
};

/**
 * 指定したローカル年月で変更がある日番号の集合を返す。
 */
export const getChangedDaysInMonth = (
  commits: readonly CommitDate[],
  monthKey: string
): Set<number> => {
  if (!MONTH_KEY_PATTERN.test(monthKey)) {
    return new Set();
  }

  const days = new Set<number>();

  commits.forEach(commit => {
    const date = parseCommitDate(commit.date);
    if (date && toLocalMonthKey(date) === monthKey) {
      days.add(date.getDate());
    }
  });

  return days;
};

/**
 * 指定したローカル日付のコミットを、入力された新しい順のまま返す。
 */
export const getCommitsOnDate = <T extends CommitDate>(
  commits: readonly T[],
  targetDate: Date | number
): T[] => {
  const date = targetDate instanceof Date ? targetDate : new Date(targetDate);
  if (!Number.isFinite(date.getTime())) {
    return [];
  }

  const targetYear = date.getFullYear();
  const targetMonth = date.getMonth();
  const targetDay = date.getDate();

  return commits.filter(commit => {
    const commitDate = parseCommitDate(commit.date);
    return (
      commitDate !== null &&
      commitDate.getFullYear() === targetYear &&
      commitDate.getMonth() === targetMonth &&
      commitDate.getDate() === targetDay
    );
  });
};

/**
 * 現在月から指定方向に最も近い、変更がある月を返す。
 */
export const findAdjacentChangedMonth = (
  changedMonths: readonly string[],
  currentMonth: string,
  direction: ChangedMonthDirection
): string | null => {
  if (!MONTH_KEY_PATTERN.test(currentMonth)) {
    return null;
  }

  const validMonths = [...new Set(changedMonths.filter(month => MONTH_KEY_PATTERN.test(month)))];

  if (direction === 'older') {
    return (
      validMonths
        .filter(month => month < currentMonth)
        .sort((left, right) => right.localeCompare(left))[0] ?? null
    );
  }

  return (
    validMonths
      .filter(month => month > currentMonth)
      .sort((left, right) => left.localeCompare(right))[0] ?? null
  );
};
